import { useEffect, useState, useRef, useLayoutEffect, type SyntheticEvent } from "react";
import { useSearchParams } from "react-router-dom";
import {
  X,
  RotateCw,
  ZoomIn,
  ZoomOut,
  Download,
  Eye,
  CircleArrowLeft,
  CircleArrowRight,
} from "lucide-react";
import { isImageFile, isImageServingUrl, isDocumentFile } from "@/lib/utils/filePreview";
import { getZoomShellDimensions } from "./zoomShell";
import type { BaseSize } from "./types";
import "./FileHandlers.css";

/* eslint-disable react-hooks/set-state-in-effect -- Preview state is derived from URL search params on load/change. */

interface FileHandlersProps {
  filesList?: string[];
  currentIndex?: number;
}

const PREVIEW_TITLE = "Liberty Rent";
const PREVIEW_FAVICON_HREF = "/libertyrent_icon.png";

function readElementBaseSize(el: HTMLElement | null): BaseSize | null {
  if (!el) return null;
  const width = el.offsetWidth;
  const height = el.offsetHeight;
  if (width <= 0 || height <= 0) return null;
  return { width, height };
}

export function FileHandlers({
  filesList: propFilesList = [],
  currentIndex = 0,
}: FileHandlersProps) {
  const [searchParams] = useSearchParams();
  const [filePath, setFilePath] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [isImage, setIsImage] = useState(false);
  const [showActual, setShowActual] = useState(false);
  const [currentindex, setCurrentindex] = useState(currentIndex);
  const [transform, setTransform] = useState({ rotate: 0, scale: 1 });
  const [filesList, setFilesList] = useState<string[]>(propFilesList);
  const [imageBaseSize, setImageBaseSize] = useState<BaseSize | null>(null);
  const [iframeBaseSize, setIframeBaseSize] = useState<BaseSize | null>(null);
  const retryCountRef = useRef(0);
  const [useIframeFallback, setUseIframeFallback] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const filesListInitializedRef = useRef(false);

  /** Iframe / document preview — transform wrapper (Angular file-handlers toolbar parity). */
  const [iframeRotate, setIframeRotate] = useState(0);
  const [iframeScale, setIframeScale] = useState(1);
  const [iframeFit, setIframeFit] = useState(true);

  useEffect(() => {
    document.title = PREVIEW_TITLE;

    document
      .querySelectorAll<HTMLLinkElement>('link[rel*="icon"]')
      .forEach((link) => link.remove());

    const icon = document.createElement("link");
    icon.rel = "icon";
    icon.type = "image/png";
    icon.href = PREVIEW_FAVICON_HREF;
    document.head.appendChild(icon);
  }, []);

  // Extract filesList from URL parameter or use prop (only once)
  useEffect(() => {
    if (filesListInitializedRef.current) return;

    const filesListParam = searchParams.get("filesList");
    if (filesListParam) {
      try {
        const decoded = decodeURIComponent(filesListParam);
        const parsed = JSON.parse(decoded);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setFilesList(parsed);
          filesListInitializedRef.current = true;
        }
      } catch {
        // Failed to parse filesList from URL
      }
    } else if (propFilesList.length > 0) {
      setFilesList(propFilesList);
      filesListInitializedRef.current = true;
    }
  }, [searchParams, propFilesList]);

  // Extract file path from URL query parameter
  useEffect(() => {
    const path = searchParams.get("path");
    const name = searchParams.get("name");

    if (path) {
      let decodedPath = decodeURIComponent(path);

      // Some legacy/application flows can pass the old Angular file-handlers URL
      // as the path. Unwrap it so this React previewer receives the real file URL.
      if (decodedPath.includes("/file-handlers")) {
        try {
          const nestedUrl = new URL(decodedPath, window.location.origin);
          const innerPath = nestedUrl.searchParams.get("path");
          if (innerPath) {
            decodedPath = innerPath;
          }
        } catch {
          const match = decodedPath.match(/[?&]path=([^&]+)/);
          if (match) {
            decodedPath = decodeURIComponent(match[1]);
          }
        }
      }

      // Don't apply URL fixes to blob URLs (e.g. newly uploaded PDFs) - they must stay intact for preview
      const isBlobUrl = decodedPath.startsWith("blob:");
      if (!isBlobUrl) {
        // Fix URL encoding issues - replace incorrectly encoded colons
        // Only fix if colon is not followed by double slash (to avoid https:////)
        if (decodedPath.match(/^https?:[^/]/)) {
          decodedPath = decodedPath.replace(/^(https?):([^/])/, "$1://$2");
        }
        // Also fix if there are triple or more slashes
        decodedPath = decodedPath.replace(/^(https?:\/\/)\/+/g, "$1");
      }

      // Ensure the URL is properly formatted
      // If it's a serve-file-preview URL, make sure it's a valid URL
      if (decodedPath.includes("/serve-file-preview/")) {
        try {
          // Validate URL format
          const url = new URL(decodedPath);
          decodedPath = url.toString();
        } catch {
          // If URL parsing fails, try to fix common issues
          if (
            !decodedPath.startsWith("http://") &&
            !decodedPath.startsWith("https://")
          ) {
            // Add protocol if missing
            decodedPath = "https://" + decodedPath;
          }
          // Try to create URL again after fixing
          try {
            const url = new URL(decodedPath);
            decodedPath = url.toString();
          } catch {
            // If still fails, silently continue
          }
        }
      }

      // Reset retry count and iframe fallback when path changes
      retryCountRef.current = 0;
      setUseIframeFallback(false);

      // Validate the URL before setting it
      if (decodedPath) {
        // For serve-file-preview URLs, ensure they're complete and valid
        if (decodedPath.includes("/serve-file-preview/")) {
          // Make sure the URL is complete
          if (
            !decodedPath.startsWith("http://") &&
            !decodedPath.startsWith("https://")
          ) {
            // This shouldn't happen, but handle it
            return;
          }
        }
        setFilePath(decodedPath);

        // Extract filename from path or use provided name
        let extractedFileName = "File Preview";
        if (name) {
          extractedFileName = decodeURIComponent(name);
          setFileName(extractedFileName);
        } else {
          // Try to extract filename from path
          const pathParts = decodedPath.split("/");
          const lastPart = pathParts[pathParts.length - 1];
          extractedFileName = lastPart.split("?")[0]; // Remove query params
          setFileName(extractedFileName || "File Preview");
        }

        // Determine if file is an image
        // For blob URLs (e.g. newly uploaded DL/Passport PDF), use fileName so PDFs open in iframe, not as image
        const fileNameToCheck = extractedFileName || decodedPath;
        const documentByExtension = isDocumentFile(fileNameToCheck);
        const imageByExtension = isImageFile(fileNameToCheck);
        const imageByUrl = isImageServingUrl(decodedPath);
        // Blob URLs for documents (PDF, etc.) must never be treated as image so preview works in iframe
        const isBlobDocument = isBlobUrl && isDocumentFile(fileNameToCheck);
        const detectedAsImage =
          documentByExtension || isBlobDocument
            ? false
            : imageByExtension || imageByUrl;
        setIsImage(detectedAsImage);

        // Angular file-handlers: PDFs and tokenized report URLs open directly in the window.
        const shouldOpenReportDirectly =
          !isBlobUrl &&
          !detectedAsImage &&
          (decodedPath.includes("access_token=") ||
            /\/api\/report\//i.test(decodedPath) ||
            /\/api\/credit_report\//i.test(decodedPath) ||
            documentByExtension);

        if (shouldOpenReportDirectly) {
          const directUrl = `${decodedPath}${decodedPath.includes("?") ? "&" : "?"}time=${Date.now()}`;
          window.location.replace(directUrl);
          return;
        }
      }
    }
  }, [searchParams]);

  useEffect(() => {
    setIframeRotate(0);
    setIframeScale(1);
    setIframeFit(true);
    setIframeBaseSize(null);
    setTransform({ rotate: 0, scale: 1 });
    setImageBaseSize(null);
    setShowActual(false);
  }, [filePath]);

  useLayoutEffect(() => {
    if (imageBaseSize || !imageRef.current?.complete) return;
    const measured = readElementBaseSize(imageRef.current);
    if (measured) setImageBaseSize(measured);
  }, [showActual, filePath, imageBaseSize]);

  useLayoutEffect(() => {
    if (iframeBaseSize || !iframeRef.current) return;
    const measured = readElementBaseSize(iframeRef.current);
    if (measured) setIframeBaseSize(measured);
  }, [iframeFit, filePath, iframeBaseSize, useIframeFallback, isImage]);

  // Initialize filesList with current filePath only if filesList is empty and wasn't set from URL/props
  useEffect(() => {
    if (
      filePath &&
      filesList.length === 0 &&
      !filesListInitializedRef.current
    ) {
      setFilesList([filePath]);
      setCurrentindex(0);
      filesListInitializedRef.current = true;
    }
  }, [filePath]); // Only depend on filePath to avoid loop

  // Update currentindex when filePath changes (use ref to avoid dependency on filesList array)
  const filesListRef = useRef(filesList);
  useEffect(() => {
    filesListRef.current = filesList;
  }, [filesList]);

  useEffect(() => {
    if (!filePath || filesListRef.current.length === 0) return;

    // Helper function to normalize URLs for comparison
    const normalizeUrl = (url: string): string => {
      try {
        const urlObj = new URL(url);
        // Remove query params for comparison
        return urlObj.origin + urlObj.pathname;
      } catch {
        // If URL parsing fails, try to extract path
        const match = url.match(/https?:\/\/[^/]+(\/[^?]*)/);
        return match ? match[1] : url.split("?")[0];
      }
    };

    // Helper function to check if two URLs match
    const urlsMatch = (url1: string, url2: string): boolean => {
      // Exact match
      if (url1 === url2) return true;

      // Normalized match (without query params)
      const norm1 = normalizeUrl(url1);
      const norm2 = normalizeUrl(url2);
      if (norm1 === norm2) return true;

      // Check if one contains the other (for partial matches)
      if (url1.includes(url2) || url2.includes(url1)) return true;

      // Check file IDs in the URL (e.g., /serve-file-preview/123)
      const idMatch1 = url1.match(/\/(\d+)(?:\?|$)/);
      const idMatch2 = url2.match(/\/(\d+)(?:\?|$)/);
      if (idMatch1 && idMatch2 && idMatch1[1] === idMatch2[1]) return true;

      return false;
    };

    // Check if current filePath is already in filesList
    const index = filesListRef.current.findIndex((f) => urlsMatch(f, filePath));

    // Only update if index changed and is valid
    if (index >= 0 && index !== currentindex) {
      setCurrentindex(index);
    }
  }, [filePath, currentindex]); // Only depend on filePath and currentindex, not filesList

  // Close preview window (same as Angular / file-handlers close)
  const handleClose = () => {
    window.close();
  };

  // Rotate image
  const handleRotate = () => {
    setTransform((prev) => ({
      ...prev,
      rotate: prev.rotate + 90,
    }));
  };

  // Zoom in
  const handleZoomIn = () => {
    setTransform((prev) => ({
      ...prev,
      scale: prev.scale * 1.1,
    }));
  };

  // Zoom out
  const handleZoomOut = () => {
    setTransform((prev) => ({
      ...prev,
      scale: Math.max(0.1, prev.scale * 0.9),
    }));
  };

  // Reset transform
  const handleReset = () => {
    setTransform({ rotate: 0, scale: 1 });
  };

  const handleToggleShowActual = () => {
    setShowActual((prev) => !prev);
    handleReset();
    setImageBaseSize(null);
  };

  const handleToggleIframeFit = () => {
    setIframeFit((prev) => !prev);
    setIframeRotate(0);
    setIframeScale(1);
    setIframeBaseSize(null);
  };

  const handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    retryCountRef.current = 0;
    const measured = readElementBaseSize(event.currentTarget);
    if (measured) setImageBaseSize(measured);
  };

  const handleIframeLoad = () => {
    const measured = readElementBaseSize(iframeRef.current);
    if (measured) setIframeBaseSize(measured);
  };

  /** Same list order as Angular `next()` / `previous()` — advance by index, not by image type. */
  const canPrevFile = filesList.length > 1 && currentindex > 0;
  const canNextFile =
    filesList.length > 1 && currentindex < filesList.length - 1;

  const navigateToFileAtIndex = (index: number) => {
    if (index < 0 || index >= filesList.length) return;
    const path = filesList[index];
    setCurrentindex(index);
    setFilePath(path);
    const rawName = path.split("/").pop()?.split("?")[0] || "";
    let displayName = rawName;
    try {
      displayName = decodeURIComponent(rawName);
    } catch {
      displayName = rawName;
    }
    setFileName(displayName || "File Preview");

    const blob = path.startsWith("blob:");
    const fileNameToCheck = displayName || path;
    const documentByExtension = isDocumentFile(fileNameToCheck);
    const imageByExtension = isImageFile(fileNameToCheck);
    const imageByUrl = isImageServingUrl(path);
    const isBlobDocument = blob && isDocumentFile(fileNameToCheck);
    const detectedAsImage = isBlobDocument
      ? false
      : documentByExtension
        ? false
        : imageByExtension || imageByUrl;
    setIsImage(detectedAsImage);
    setUseIframeFallback(false);
    handleReset();
  };

  const handlePrevFile = () => {
    if (!canPrevFile) return;
    navigateToFileAtIndex(currentindex - 1);
  };

  const handleNextFile = () => {
    if (!canNextFile) return;
    navigateToFileAtIndex(currentindex + 1);
  };

  // Get transform style
  const getTransformStyle = () => {
    return {
      transform: `rotate(${transform.rotate}deg) scale(${transform.scale})`,
      transition: "transform 0.3s ease",
      ...(imageBaseSize
        ? {
            width: imageBaseSize.width,
            height: imageBaseSize.height,
            maxWidth: "none" as const,
          }
        : {}),
    };
  };

  const imageZoomShellStyle = imageBaseSize
    ? getZoomShellDimensions(
        imageBaseSize.width,
        imageBaseSize.height,
        transform.scale,
        transform.rotate,
      )
    : undefined;

  const iframeZoomShellStyle = iframeBaseSize
    ? getZoomShellDimensions(
        iframeBaseSize.width,
        iframeBaseSize.height,
        iframeScale,
        iframeRotate,
      )
    : undefined;

  const isBlobUrl = filePath.startsWith("blob:");
  /** PDF / non-image iframe — same shell as images so the vertical toolbar always shows. */
  const docMode = !isImage || useIframeFallback;
  const downloadHref = isBlobUrl
    ? filePath
    : `${filePath}${filePath.includes("?") ? "&" : "?"}download=true`;

  if (!filePath) {
    return (
      <div className="file-preview-page">
        <header className="file-preview-topbar">
          <h1 className="file-preview-title">FILE PREVIEW</h1>
        </header>
        <div className="file-preview-canvas" />
      </div>
    );
  }

  return (
    <div className="file-preview-page">
      <header className="file-preview-topbar">
        <h1 className="file-preview-title">FILE PREVIEW</h1>
      </header>
      <div className="file-preview-canvas" onClick={handleClose}>
        <aside
          className="file-preview-toolbar-v"
          onClick={(e) => e.stopPropagation()}
          aria-label="Preview actions"
        >
          <button
            type="button"
            className="file-preview-tool-btn"
            onClick={handleClose}
            title="Close"
            aria-label="Close"
          >
            <X className="h-6 w-6" strokeWidth={2.5} />
          </button>
          {docMode ? (
            <button
              type="button"
              className="file-preview-tool-btn"
              onClick={() => setIframeRotate((r) => (r + 90) % 360)}
              title="Rotate"
              aria-label="Rotate"
            >
              <RotateCw className="h-5 w-5" />
            </button>
          ) : (
            <button
              type="button"
              className="file-preview-tool-btn"
              onClick={handleRotate}
              title="Rotate"
              aria-label="Rotate"
            >
              <RotateCw className="h-5 w-5" />
            </button>
          )}
          {docMode ? (
            <>
              <button
                type="button"
                className="file-preview-tool-btn"
                onClick={() => setIframeScale((s) => Math.min(4, s * 1.1))}
                title="Zoom in"
                aria-label="Zoom in"
              >
                <ZoomIn className="h-5 w-5" />
              </button>
              <button
                type="button"
                className="file-preview-tool-btn"
                onClick={() => setIframeScale((s) => Math.max(0.25, s * 0.9))}
                title="Zoom out"
                aria-label="Zoom out"
              >
                <ZoomOut className="h-5 w-5" />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="file-preview-tool-btn"
                onClick={handleZoomIn}
                title="Zoom in"
                aria-label="Zoom in"
              >
                <ZoomIn className="h-5 w-5" />
              </button>
              <button
                type="button"
                className="file-preview-tool-btn"
                onClick={handleZoomOut}
                title="Zoom out"
                aria-label="Zoom out"
              >
                <ZoomOut className="h-5 w-5" />
              </button>
            </>
          )}
          <a
            href={downloadHref}
            download={isBlobUrl ? fileName || "download" : undefined}
            className="file-preview-tool-btn"
            title="Download"
            aria-label="Download"
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="h-5 w-5" />
          </a>
          {docMode ? (
            <button
              type="button"
              className="file-preview-tool-btn"
              onClick={handleToggleIframeFit}
              title={iframeFit ? "Show larger preview" : "Fit to window"}
              aria-label="Toggle view size"
            >
              <Eye className="h-5 w-5" />
            </button>
          ) : (
            <button
              type="button"
              className="file-preview-tool-btn"
              onClick={handleToggleShowActual}
              title={
                showActual ? "Show constrained size" : "Show actual size"
              }
              aria-label="Toggle view size"
            >
              <Eye className="h-5 w-5" />
            </button>
          )}
          {filesList.length > 1 && (
            <>
              <div className="file-preview-toolbar-divider" aria-hidden />
              <button
                type="button"
                className="file-preview-tool-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevFile();
                }}
                disabled={!canPrevFile}
                title={canPrevFile ? "Previous file" : "No previous file"}
                aria-label="Previous file"
              >
                <CircleArrowLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                className="file-preview-tool-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNextFile();
                }}
                disabled={!canNextFile}
                title={canNextFile ? "Next file" : "No next file"}
                aria-label="Next file"
              >
                <CircleArrowRight className="h-5 w-5" />
              </button>
            </>
          )}
        </aside>

        {docMode ? (
          <div
            className="file-preview-scroll-area"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="file-preview-iframe-wrap">
              <div
                className="file-preview-zoom-shell"
                style={
                  iframeZoomShellStyle
                    ? {
                        width: iframeZoomShellStyle.width,
                        height: iframeZoomShellStyle.height,
                      }
                    : undefined
                }
              >
                <div
                  className="file-preview-iframe-transform"
                  style={{
                    transform: `rotate(${iframeRotate}deg) scale(${iframeScale})`,
                    transition: "transform 0.2s ease",
                    ...(iframeBaseSize
                      ? {
                          width: iframeBaseSize.width,
                          height: iframeBaseSize.height,
                        }
                      : {}),
                  }}
                >
                  <iframe
                    ref={iframeRef}
                    src={filePath}
                    title={fileName || "File Preview"}
                    onLoad={handleIframeLoad}
                    className={
                      iframeFit
                        ? "file-preview-iframe file-preview-iframe--fit"
                        : "file-preview-iframe file-preview-iframe--actual"
                    }
                    style={
                      iframeBaseSize
                        ? {
                            width: iframeBaseSize.width,
                            height: iframeBaseSize.height,
                            maxWidth: "none",
                            maxHeight: "none",
                          }
                        : undefined
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="file-preview-scroll-area"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-img-box">
              <div
                className="file-preview-zoom-shell"
                style={
                  imageZoomShellStyle
                    ? {
                        width: imageZoomShellStyle.width,
                        height: imageZoomShellStyle.height,
                      }
                    : undefined
                }
              >
                <img
                  key={filePath}
                  ref={imageRef}
                  src={filePath}
                  alt="Preview"
                  className={`preview-image ${showActual ? "img-content" : "max-image"}${imageBaseSize ? " preview-image--sized" : ""}`}
                  style={getTransformStyle()}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    retryCountRef.current += 1;

                    if (retryCountRef.current <= 1 && target.src === filePath) {
                      setTimeout(() => {
                        const currentSrc = target.src;
                        target.src =
                          "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
                        setTimeout(() => {
                          target.src = currentSrc;
                        }, 100);
                      }, 500);
                    } else if (filePath.includes("/serve-file-preview/")) {
                      setUseIframeFallback(true);
                    }
                  }}
                  onLoad={handleImageLoad}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
